import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { HiOutlineClipboardList } from "react-icons/hi";
import ConfirmDialog from "../../Elements/ConfirmDialog";

// El acta que firma el socio al darse de alta.
//
// Hasta F1 el acta se dibujaba en el backend (`utils/acta_pdf.py`) con los textos
// legales de Kong House: TODOS los clubes hacían firmar el papel de Kong con su
// nombre encima. Aquí el club sube su propio PDF. (ACTA_FIRMA_PLAN.md)
//
// Vive en su propio componente y no dentro de Settings.jsx porque es la pantalla
// que decide qué documento legal firma un socio: merece poder probarse sola, sin
// arrastrar los planes, la facturación y el consumo de IA.
export default function ActaPlantillaCard({ token, venueID, puedeGestionar, onError }) {
  const [docs, setDocs] = useState([]);
  const [trabajando, setTrabajando] = useState(false);
  // El error se pinta AQUÍ dentro. Antes subía al banner del principio de
  // Configuración, a ~700 líneas de scroll: el dueño subía un PDF de 8 MB, se
  // rechazaba, y en pantalla no cambiaba nada.
  const [aviso, setAviso] = useState('');
  // Distinto de "no hay plantilla": si la consulta FALLA no se puede afirmar que
  // el club no tenga acta propia — un 502 en pleno despliegue pintaba el aviso
  // alarmista. El caso real de club sin plantilla es un 200 con [].
  const [fallo, setFallo] = useState(false);
  // Quitar la plantilla devuelve al club al acta genérica del sistema sin que se
  // note en ningún sitio: los socios que se den de alta a partir de ese momento
  // firman otro documento. Eso se pregunta, no se hace de un clic.
  const [confirmandoQuitar, setConfirmandoQuitar] = useState(false);

  const cargar = useCallback(async () => {
    if (!token || !venueID) return;
    try {
      const res = await axios.get(`/v1/venues/${venueID}/documents`, {
        params: { kind: "acta" },
        headers: { Authorization: `Bearer ${token}` },
      });
      setDocs(Array.isArray(res.data) ? res.data : []);
      setFallo(false);
    } catch {
      setDocs([]);
      setFallo(true);
    }
  }, [token, venueID]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const activa = docs.find((d) => d.active) || null;
  const historico = docs.filter((d) => !d.active);

  const avisar = async (err, porDefecto) => {
    const data = err?.response?.data;
    let detalle = porDefecto;
    try {
      // Con `responseType: 'blob'` el cuerpo del error es un Blob, no el JSON:
      // leer `.detail` a secas daba siempre undefined.
      if (data instanceof Blob) detalle = JSON.parse(await data.text())?.detail || porDefecto;
      else detalle = data?.detail || porDefecto;
    } catch { /* nos quedamos con el mensaje por defecto */ }
    setAviso(detalle);
    onError?.(detalle);
  };

  const subir = async (file) => {
    if (!token || !venueID || !file) return;
    const form = new FormData();
    form.append("file", file);
    form.append("kind", "acta");
    try {
      setTrabajando(true);
      setAviso("");
      onError?.("");
      await axios.post(`/v1/venues/${venueID}/documents`, form, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await cargar();
    } catch (err) {
      await avisar(err, "No se pudo subir el acta.");
    } finally {
      setTrabajando(false);
    }
  };

  const ver = async (docId) => {
    // El PDF vive en storage privado: se pide con el token y se abre desde un
    // blob. Nunca una URL de S3 cruda en el navegador.
    //
    // La pestaña se abre ANTES del await: Safari de iPad (la tablet del club)
    // bloquea el `window.open` que no cuelga directamente del clic, y el botón
    // se quedaba sin hacer nada.
    const ventana = window.open("", "_blank");
    try {
      const res = await axios.get(`/v1/venues/${venueID}/documents/${docId}/file`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      if (ventana) ventana.location = url;
      else window.open(url, "_blank", "noopener");
      // Se suelta cuando el visor ya lo tiene: si no, cada "Ver PDF" deja el
      // documento entero retenido en memoria.
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      if (ventana) ventana.close();
      await avisar(err, "No se pudo abrir el acta.");
    }
  };

  const dejarDeUsar = async (docId) => {
    try {
      setTrabajando(true);
      setConfirmandoQuitar(false);
      onError?.("");
      await axios.delete(`/v1/venues/${venueID}/documents/${docId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await cargar();
    } catch (err) {
      onError?.(err?.response?.data?.detail || "No se pudo quitar el acta.");
    } finally {
      setTrabajando(false);
    }
  };

  return (
    <section className="mb-10">
      <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900 mb-4">
        <HiOutlineClipboardList className="text-purple-600" />
        Acta de inscripción
      </h2>
      <div className="rounded-xl border border-gray-200 bg-white p-4">
        <p className="text-sm text-gray-700">
          El documento que firma el socio al darse de alta. Sube el PDF de tu club —el que
          te dio tu abogado, con tus estatutos y tus normas— y se usará tal cual: no se le
          cambia ni una coma. Al firmar se le añade una última página con los datos del
          socio, su firma y la huella del documento.
        </p>

        {activa ? (
          <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-sm font-semibold text-emerald-900">
                  {activa.original_filename || "acta.pdf"}
                </div>
                <div className="text-xs text-emerald-800">
                  Versión {activa.version}
                  {activa.created_at
                    ? ` · subida el ${new Date(activa.created_at).toLocaleDateString("es-ES")}`
                    : ""}
                  {activa.byte_size ? ` · ${(activa.byte_size / 1024).toFixed(0)} KB` : ""}
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => ver(activa.id)}
                  className="rounded-lg border border-emerald-300 bg-white px-3 py-1.5 text-sm font-medium text-emerald-800 hover:bg-emerald-50"
                >
                  Ver PDF
                </button>
                {puedeGestionar && (
                  <button
                    type="button"
                    disabled={trabajando}
                    onClick={() => setConfirmandoQuitar(true)}
                    className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    Dejar de usarla
                  </button>
                )}
              </div>
            </div>
          </div>
        ) : fallo ? (
          <div className="mt-3 rounded-lg border border-gray-300 bg-gray-50 px-4 py-3 text-sm text-gray-700">
            No se ha podido consultar el acta del club. Recarga la página: hasta saberlo,
            no podemos decirte qué documento se está firmando.
          </div>
        ) : (
          <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            Tu club <strong>no tiene acta propia</strong>: se está firmando el acta genérica
            del sistema. Sube tu PDF para que el socio firme el documento de tu club.
          </div>
        )}

        {aviso && (
          <div className="mt-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            {aviso}
          </div>
        )}

        {puedeGestionar && (
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg bg-purple-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-purple-700">
              <input
                type="file"
                accept="application/pdf"
                className="hidden"
                aria-label="Subir acta de inscripción en PDF"
                disabled={trabajando}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  // El input se limpia SIEMPRE: si no, volver a elegir el mismo
                  // fichero tras un error no dispara onChange y parece colgado.
                  e.target.value = "";
                  if (file) subir(file);
                }}
              />
              {trabajando ? "Subiendo..." : activa ? "Subir versión nueva" : "Subir acta en PDF"}
            </label>
            <span className="text-xs text-gray-500">PDF, máximo 5 MB.</span>
          </div>
        )}

        <ConfirmDialog
          active={confirmandoQuitar}
          tone="danger"
          title={`Dejar de usar ${activa?.original_filename || "el acta del club"}`}
          description="Los socios que se den de alta a partir de ahora firmarán el acta genérica del sistema, no la de tu club."
          note="Las actas ya firmadas no cambian: siguen guardadas con el documento que se firmó."
          confirmLabel="Sí, volver a la genérica"
          busy={trabajando}
          onConfirm={() => activa && dejarDeUsar(activa.id)}
          onCancel={() => setConfirmandoQuitar(false)}
        />

        {historico.length > 0 && (
          <div className="mt-4 border-t border-gray-200 pt-3">
            <div className="text-xs font-semibold text-gray-500">Versiones anteriores</div>
            <p className="mt-1 text-xs text-gray-500">
              No se borran: un socio que firmó con una versión anterior tiene derecho a que se
              pueda demostrar qué texto firmó.
            </p>
            <ul className="mt-2 space-y-1">
              {historico.map((doc) => (
                <li key={doc.id} className="flex items-center justify-between gap-3 text-sm text-gray-600">
                  <span className="min-w-0 truncate">
                    v{doc.version} · {doc.original_filename || "acta.pdf"}
                    {doc.created_at ? ` · ${new Date(doc.created_at).toLocaleDateString("es-ES")}` : ""}
                  </span>
                  <button
                    type="button"
                    onClick={() => ver(doc.id)}
                    className="shrink-0 text-sm font-medium text-purple-700 hover:underline"
                  >
                    Ver PDF
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </section>
  );
}
